import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  Paper,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CardMembership as CardIcon,
  Event as EventIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

const UserPassesComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [allUsers, setAllUsersState] = useState([]);
  const [userPassesCache, setUserPassesCache] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPass, setEditingPass] = useState(null);
  const [editPassDialogOpen, setEditPassDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPasses, setLoadingPasses] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // State variables for adding pass
  const [passTypes, setPassTypes] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [selectedPassType, setSelectedPassType] = useState(null);
  const [lessonCategoryCounts, setLessonCategoryCounts] = useState({});
  const [addPassDialogOpen, setAddPassDialogOpen] = useState(false);
  const [addPassError, setAddPassError] = useState("");
  const [addPassSuccess, setAddPassSuccess] = useState("");
  const [isAddingPass, setIsAddingPass] = useState(false);

  // NEW: state for "payed" checkbox (defaults to true)
  const [payedValue, setPayedValue] = useState(true);

  // State variables for duration
  const [durationValue, setDurationValue] = useState("");
  const [durationUnit, setDurationUnit] = useState("days");

  // Month availability state
  const [monthRestricted, setMonthRestricted] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Purchase period availability state
  const [purchasePeriodRestricted, setPurchasePeriodRestricted] = useState(false);
  const [purchaseAvailableFrom, setPurchaseAvailableFrom] = useState("");
  const [purchaseAvailableUntil, setPurchaseAvailableUntil] = useState("");

  // State variable for selected user when adding pass
  const [selectedUserForPass, setSelectedUserForPass] = useState(null);

  // State to track if duration fields should be hidden due to pass type constraints
  const [shouldHideDuration, setShouldHideDuration] = useState(false);

  // State for event pass handling
  const [isEventPassMode, setIsEventPassMode] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Filter state variables
  const [filterActive, setFilterActive] = useState(null);
  const [filterPayed, setFilterPayed] = useState(null);
  const [filterCreatedByAdmin, setFilterCreatedByAdmin] = useState(null);
  const [filterPassType, setFilterPassType] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchPassTypesAndLessonCategories();
    fetchAllUsers();
  }, []);

  // Fetch events when dialog opens and event pass mode is selected
  useEffect(() => {
    if (addPassDialogOpen && isEventPassMode) {
      fetchAvailableEvents();
    }
  }, [addPassDialogOpen, isEventPassMode]);

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      
      let headers;
      try {
        headers = await getAuthHeaders();
      } catch (authError) {
        console.error('Authentication error:', authError);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user`,
        { headers }
      );

      if (response.status === 200 && Array.isArray(response.data.objects)) {
        setAllUsersState(response.data.objects);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if duration fields should be hidden
  const shouldHideDurationFields = (passType) => {
    if (!passType) return false;

    // Hide duration fields if pass type has month restrictions or purchase period restrictions
    return passType.month_restricted || passType.purchase_period_restricted;
  };

  const fetchPassTypesAndLessonCategories = async () => {
    try {
      let headers;
      try {
        headers = await getAuthHeaders();
      } catch (authError) {
        console.error('Authentication error:', authError);
        return;
      }
      
      const [passTypeResponse, lessonCategoryResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, {
          headers
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, {
          headers
        }),
      ]);

      if (
        passTypeResponse.status === 200 &&
        Array.isArray(passTypeResponse.data.objects)
      ) {
        setPassTypes(passTypeResponse.data.objects);
      } else {
        throw new Error("Failed to fetch pass types");
      }

      if (
        lessonCategoryResponse.status === 200 &&
        Array.isArray(lessonCategoryResponse.data.objects)
      ) {
        setLessonCategories(lessonCategoryResponse.data.objects);
      } else {
        throw new Error("Failed to fetch lesson categories");
      }
    } catch (error) {
      console.error("Error fetching pass types and lesson categories:", error);
    }
  };

  const fetchAvailableEvents = async () => {
    setLoadingEvents(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events?active_only=true`,
        { headers }
      );
      if (response.status === 200) {
        setAvailableEvents(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setAvailableEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchUserPasses = async (userId) => {
    try {
      setLoadingPasses(true);

      let headers;
      try {
        headers = await getAuthHeaders();
      } catch (authError) {
        console.error('Authentication error:', authError);
        setLoadingPasses(false);
        return;
      }

      // Build query parameters
      const params = new URLSearchParams({ user_id: userId });

      if (filterActive !== null) params.append("active", filterActive);
      if (filterPayed !== null) params.append("payed", filterPayed);
      if (filterCreatedByAdmin !== null) params.append("created_by_admin", filterCreatedByAdmin);
      if (filterPassType) params.append("pass_type_id", filterPassType);
      if (filterDateFrom) params.append("date_from", new Date(filterDateFrom).toISOString());
      if (filterDateTo) params.append("date_to", new Date(filterDateTo).toISOString());
      if (sortBy) {
        params.append("sort_by", sortBy);
        params.append("sort_order", sortOrder);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/all_for_admin?${params.toString()}`,
        { headers }
      );

      if (response.status === 200) {
        setUserPassesCache(prev => ({
          ...prev,
          [userId]: response.data.objects
        }));
      }
    } catch (error) {
      console.error("Failed to fetch user passes:", error);
    } finally {
      setLoadingPasses(false);
    }
  };

  const invalidateUserCache = (userId) => {
    setUserPassesCache(prev => {
      const newCache = { ...prev };
      delete newCache[userId];
      return newCache;
    });
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setDialogOpen(true);

    // Fetch passes for this user if not cached
    if (!userPassesCache[user.id]) {
      await fetchUserPasses(user.id);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleEditPass = (pass) => {
    setEditingPass(pass);
    setEditPassDialogOpen(true);
  };

  const handleDeletePass = (pass) => {
    if (window.confirm("Czy na pewno chcesz usunąć ten karnet?")) {
      deletePass(pass.id);
    }
  };

  const deletePass = async (passId) => {
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/${passId}`,
        { headers }
      );
      // Invalidate cache and refetch passes for this user
      invalidateUserCache(selectedUser.id);
      await fetchUserPasses(selectedUser.id);
    } catch (error) {
      console.error("Failed to delete pass:", error);
    }
  };

  const handleSaveEditPass = async () => {
    try {
      setIsUpdating(true);

      // Create a copy of editingPass to modify
      const passToUpdate = { ...editingPass };

      // Convert valid_until to ISO datetime format if it exists
      if (passToUpdate.valid_until) {
        const date = new Date(passToUpdate.valid_until);
        passToUpdate.valid_until = date.toISOString();
      }

      const headers = await getAuthHeaders();
      await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/${editingPass.id}`,
        passToUpdate,
        { headers }
      );

      // Invalidate cache and refetch passes for this user
      invalidateUserCache(selectedUser.id);
      await fetchUserPasses(selectedUser.id);

      setEditPassDialogOpen(false);
      setEditingPass(null);
      setIsUpdating(false);
    } catch (error) {
      console.error("Failed to update pass:", error);
      setIsUpdating(false);
    }
  };

  const handlePassTypeChange = (event) => {
    const selectedPass = event.target.value;
    setSelectedPassType(selectedPass);

    // Check if this is an event pass
    const isEventPass = selectedPass.is_event_pass === true;
    setIsEventPassMode(isEventPass);

    if (isEventPass) {
      // For event passes, clear lesson category counts and hide duration
      setLessonCategoryCounts({});
      setShouldHideDuration(true);
      setDurationValue("");
      setDurationUnit("days");
    } else {
      // Reset lessons count for new pass type
      const newLessonsCount = {};
      if (selectedPass.lesson_categories_for_pass_type) {
        selectedPass.lesson_categories_for_pass_type.forEach((category) => {
          newLessonsCount[category.lesson_category_id] = category.lessons_to_use;
        });
      }
      setLessonCategoryCounts(newLessonsCount);

      // Check if duration fields should be hidden for this pass type
      const hideDuration = shouldHideDurationFields(selectedPass);
      setShouldHideDuration(hideDuration);

      // Set default duration from backend only if duration fields are not hidden
      if (!hideDuration && selectedPass.valid_duration) {
        // valid_duration comes as total seconds (float) from FastAPI/Pydantic
        const durationSeconds = selectedPass.valid_duration;
        const days = Math.round(durationSeconds / (24 * 60 * 60));

        if (days > 0) {
          if (days % 7 === 0) {
            // If divisible by 7, show as weeks
            setDurationValue((days / 7).toString());
            setDurationUnit("weeks");
          } else {
            // Otherwise show as days
            setDurationValue(days.toString());
            setDurationUnit("days");
          }
        }
      } else if (hideDuration) {
        // Clear duration values when hiding fields
        setDurationValue("");
        setDurationUnit("days");
      }
    }
  };

  const handleLessonCountChange = (lessonCategoryId, count) => {
    setLessonCategoryCounts((prevState) => ({
      ...prevState,
      [lessonCategoryId]: count,
    }));
  };

  const handleMonthToggle = (monthNumber) => {
    setAvailableMonths(prev =>
      prev.includes(monthNumber)
        ? prev.filter(m => m !== monthNumber)
        : [...prev, monthNumber]
    );
  };

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const getLessonCategoryName = (categoryId) => {
    const category = lessonCategories.find((cat) => cat.id === categoryId);
    return category ? category.name : `ID: ${categoryId}`;
  };

  const handleAddPass = async () => {
    setIsAddingPass(true);
    setAddPassError("");
    setAddPassSuccess("");

    try {
      // Skip duration validation if fields are hidden (auto-calculated by backend)
      if (!shouldHideDuration && (!durationValue || parseInt(durationValue, 10) <= 0)) {
        setAddPassError("Proszę wprowadzić poprawną długość trwania.");
        setIsAddingPass(false);
        return;
      }

      // Validate month restriction
      if (monthRestricted && availableMonths.length === 0) {
        setAddPassError("Proszę wybrać co najmniej jeden miesiąc dla karnetów z ograniczeniem miesięcznym.");
        setIsAddingPass(false);
        return;
      }

      // Validate purchase period restriction
      if (purchasePeriodRestricted) {
        if (!purchaseAvailableFrom && !purchaseAvailableUntil) {
          setAddPassError("Proszę określić okres dostępności zakupu.");
          setIsAddingPass(false);
          return;
        }
        if (purchaseAvailableFrom && purchaseAvailableUntil &&
            new Date(purchaseAvailableFrom) >= new Date(purchaseAvailableUntil)) {
          setAddPassError("Data rozpoczęcia musi być wcześniejsza niż data zakończenia.");
          setIsAddingPass(false);
          return;
        }
      }

      const lessonsCount = selectedPassType.lesson_categories_for_pass_type.map((category) => ({
        lesson_category_id: category.lesson_category_id,
        lessons_to_use: category.is_unlimited ? 0 : parseInt(lessonCategoryCounts[category.lesson_category_id] || category.lessons_to_use, 10),
        is_unlimited: category.is_unlimited || false,
      }));

      const valid_from = new Date(); // Current date and time
      const valid_from_iso = valid_from.toISOString();

      // Calculate valid_until only if duration fields are visible (not auto-calculated)
      let valid_until_iso = null;

      if (!shouldHideDuration) {
        // Manual duration calculation when fields are visible
        let valid_until = new Date(valid_from);
        const durationVal = parseInt(durationValue, 10);

        if (durationUnit === "days") {
          valid_until.setDate(valid_until.getDate() + durationVal);
        } else if (durationUnit === "weeks") {
          valid_until.setDate(valid_until.getDate() + durationVal * 7);
        } else if (durationUnit === "months") {
          valid_until.setMonth(valid_until.getMonth() + durationVal);
        }

        valid_until_iso = valid_until.toISOString();
      }
      // If shouldHideDuration is true, valid_until_iso remains null for backend auto-calculation

      const userIdToUse = selectedUser
        ? selectedUser.id
        : selectedUserForPass
          ? selectedUserForPass.id
          : null;

      if (!userIdToUse) {
        setAddPassError("Proszę wybrać użytkownika.");
        setIsAddingPass(false);
        return;
      }

      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass_to_user/`,
        {
          pass_type_name: selectedPassType.name,
          user_id: userIdToUse,
          lesson_categories: lessonsCount,
          valid_from: valid_from_iso,
          valid_until: valid_until_iso,
          payed: payedValue,          // send checkbox value
          created_by_admin: true,     // always true
          // Include availability fields if they are configured
          ...(monthRestricted && {
            month_restricted: true,
            available_months: availableMonths,
          }),
          ...(purchasePeriodRestricted && {
            purchase_period_restricted: true,
            purchase_available_from: purchaseAvailableFrom ? new Date(purchaseAvailableFrom).toISOString() : null,
            purchase_available_until: purchaseAvailableUntil ? new Date(purchaseAvailableUntil).toISOString() : null,
          }),
        },
        { headers }
      );

      if (response.status === 200) {
        setAddPassSuccess("Karnet został dodany pomyślnie!");
        setAddPassError("");

        // Invalidate cache and refetch passes for this user
        invalidateUserCache(userIdToUse);
        if (selectedUser && selectedUser.id === userIdToUse) {
          await fetchUserPasses(userIdToUse);
        }

        handleCloseAddPassDialog();
      } else {
        setAddPassError("Nie udało się dodać karnetu.");
        setAddPassSuccess("");
      }
    } catch (error) {
      console.error("Failed to add pass:", error);
      setAddPassError("Nie udało się dodać karnetu.");
    } finally {
      setIsAddingPass(false);
    }
  };

  const handleCloseAddPassDialog = () => {
    setAddPassDialogOpen(false);
    setSelectedPassType(null);
    setLessonCategoryCounts({});
    setAddPassError("");
    setAddPassSuccess("");
    setDurationValue("");
    setDurationUnit("days");
    setSelectedUserForPass(null);
    setPayedValue(true); // reset checkbox to default
    setShouldHideDuration(false); // reset duration field visibility
    // Reset availability settings
    setMonthRestricted(false);
    setAvailableMonths([]);
    setPurchasePeriodRestricted(false);
    setPurchaseAvailableFrom("");
    setPurchaseAvailableUntil("");
    // Reset event pass mode
    setIsEventPassMode(false);
  };

  // Function to open Add Pass Dialog
  const handleOpenAddPassDialog = () => {
    setSelectedUser(null); // Clear selected user to show user selector
    setAddPassDialogOpen(true);
  };

  // Updated userColumns to show both full name and email in the user list
  const userColumns = [
    {
      field: "full_name",
      headerName: "Użytkownik",
      flex: 1,
      minWidth: isMobile ? 200 : 300,
      renderCell: (params) => (
        <div>
          {params.row.full_name}
          <br />
          <span style={{ fontSize: isMobile ? "0.7em" : "0.8em", color: "#888" }}>
            {params.row.email}
          </span>
        </div>
      ),
    },
  ];

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to calculate days remaining
  const getDaysRemaining = (validUntil) => {
    if (!validUntil) return null;
    const now = new Date();
    const expiry = new Date(validUntil);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to get expiry status color
  const getExpiryColor = (daysRemaining) => {
    if (daysRemaining === null) return "inherit";
    if (daysRemaining < 0) return "#d32f2f"; // expired - red
    if (daysRemaining <= 7) return "#ed6c02"; // expiring soon - orange
    return "#2e7d32"; // valid - green
  };

  const passColumns = [
    {
      field: "pass_type_name",
      headerName: "Typ Karnetu",
      flex: 1,
      minWidth: isMobile ? 150 : 180
    },
    {
      field: "created_at",
      headerName: "Data utworzenia",
      flex: 1,
      minWidth: isMobile ? 120 : 140,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "valid_until",
      headerName: "Ważny do",
      flex: 1,
      minWidth: isMobile ? 140 : 180,
      renderCell: (params) => {
        const daysRemaining = getDaysRemaining(params.value);
        const color = getExpiryColor(daysRemaining);
        let statusText = "";
        if (daysRemaining !== null) {
          if (daysRemaining < 0) {
            statusText = ` (wygasł ${Math.abs(daysRemaining)} dni temu)`;
          } else if (daysRemaining === 0) {
            statusText = " (wygasa dziś)";
          } else {
            statusText = ` (${daysRemaining} dni pozostało)`;
          }
        }
        return (
          <Box sx={{ color }}>
            <div>{formatDate(params.value)}</div>
            <div style={{ fontSize: '0.75em', fontWeight: 'bold' }}>{statusText}</div>
          </Box>
        );
      },
    },
    {
      field: "lessons_summary",
      headerName: "Lekcje",
      flex: 0.8,
      minWidth: isMobile ? 100 : 120,
      sortable: false,
      renderCell: (params) => {
        const hasUnlimited = params.row.lesson_categories_for_pass?.some(lc => lc.is_unlimited);
        if (hasUnlimited) {
          return <span style={{ color: '#1976d2', fontWeight: 'bold' }}>Unlimited</span>;
        }
        const remaining = params.row.remaining_lessons ?? 0;
        const total = params.row.total_lessons ?? 0;
        return `${remaining}/${total}`;
      },
    },
    {
      field: "active",
      headerName: "Aktywny",
      flex: 0.5,
      minWidth: isMobile ? 80 : 90,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: params.value ? '#e8f5e9' : '#f5f5f5',
            color: params.value ? '#2e7d32' : '#757575',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            textAlign: 'center',
          }}
        >
          {params.value ? "Tak" : "Nie"}
        </Box>
      ),
    },
    {
      field: "payed",
      headerName: "Opłacony",
      flex: 0.5,
      minWidth: isMobile ? 80 : 90,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: params.value ? '#e3f2fd' : '#ffebee',
            color: params.value ? '#1565c0' : '#c62828',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            textAlign: 'center',
          }}
        >
          {params.value ? "Tak" : "Nie"}
        </Box>
      ),
    },
    {
      field: "created_by_admin",
      headerName: "Admin",
      flex: 0.5,
      minWidth: isMobile ? 80 : 90,
      renderCell: (params) => (params.value ? "✓" : "-"),
    },
    {
      field: "id",
      headerName: "ID",
      flex: 0.4,
      minWidth: isMobile ? 70 : 80,
      renderCell: (params) => (
        <Box sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Akcje",
      sortable: false,
      filterable: false,
      minWidth: isMobile ? 100 : 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            onClick={() => handleEditPass(params.row)}
            color="primary"
            size={isMobile ? "small" : "medium"}
          >
            <EditIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton
            onClick={() => handleDeletePass(params.row)}
            color="error"
            size={isMobile ? "small" : "medium"}
          >
            <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredUsers = allUsers.filter((user) => {
    const nameMatches = user.full_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    // Note: We can't filter by paid passes here since we load passes on-demand
    // If showPaidUsersOnly is needed, it would require fetching all passes upfront
    return nameMatches;
  });

  return (
    <Box>
      {/* Header with Action Button */}
      <Box 
        display="flex" 
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between" 
        alignItems={{ xs: "stretch", sm: "center" }} 
        mb={3}
        gap={2}
      >
        <Box>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Karnety Użytkowników
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzaj karnetami użytkowników - dodawaj nowe, edytuj istniejące i monitoruj status płatności
          </Typography>
        </Box>
        <Button
          onClick={handleOpenAddPassDialog}
          color="primary"
          variant="contained"
          size="large"
          startIcon={<CardIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            whiteSpace: 'nowrap',
            minWidth: { sm: 'fit-content' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Dodaj Karnet Użytkownikowi
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Dodaj Karnet
          </Box>
        </Button>
      </Box>

      {/* Users List Section */}
      <Box mb={2}>
        <TextField
          label="Szukaj użytkownika"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          margin="normal"
          sx={{ mb: 2 }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography ml={2}>Ładowanie użytkowników...</Typography>
        </Box>
      ) : (
        <Paper elevation={2} sx={{ height: { xs: 500, sm: 600 }, width: "100%" }}>
          <DataGrid
            rows={filteredUsers}
            columns={userColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: isMobile ? 10 : 25 },
              },
            }}
            pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
            getRowId={(row) => row.id}
            onRowClick={(params) => handleUserClick(params.row)}
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:hover': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-cell': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '6px 4px', sm: '16px 8px' },
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                fontWeight: 600,
              },
              '& .MuiDataGrid-footerContainer': {
                minHeight: { xs: '40px', sm: '52px' }
              }
            }}
            density={isMobile ? "compact" : "standard"}
          />
        </Paper>
      )}

      {/* Dialog showing passes for the selected user */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Karnety użytkownika: {selectedUser?.full_name}
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {selectedUser?.email}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Filter Controls */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Filtry i sortowanie
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* First row - Status filters */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterActive === null ? "" : filterActive}
                    onChange={(e) => setFilterActive(e.target.value === "" ? null : e.target.value === "true")}
                    label="Status"
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    <MenuItem value="true">Aktywne</MenuItem>
                    <MenuItem value="false">Nieaktywne</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Płatność</InputLabel>
                  <Select
                    value={filterPayed === null ? "" : filterPayed}
                    onChange={(e) => setFilterPayed(e.target.value === "" ? null : e.target.value === "true")}
                    label="Płatność"
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    <MenuItem value="true">Opłacone</MenuItem>
                    <MenuItem value="false">Nieopłacone</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Utworzony przez</InputLabel>
                  <Select
                    value={filterCreatedByAdmin === null ? "" : filterCreatedByAdmin}
                    onChange={(e) => setFilterCreatedByAdmin(e.target.value === "" ? null : e.target.value === "true")}
                    label="Utworzony przez"
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    <MenuItem value="true">Admin</MenuItem>
                    <MenuItem value="false">Użytkownik</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Typ karnetu</InputLabel>
                  <Select
                    value={filterPassType || ""}
                    onChange={(e) => setFilterPassType(e.target.value || null)}
                    label="Typ karnetu"
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {passTypes.map((pt) => (
                      <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Second row - Date filters */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField
                  label="Data utworzenia od"
                  type="date"
                  size="small"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />

                <TextField
                  label="Data utworzenia do"
                  type="date"
                  size="small"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Sortuj według</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sortuj według"
                  >
                    <MenuItem value="created_at">Data utworzenia</MenuItem>
                    <MenuItem value="valid_until">Data ważności</MenuItem>
                    <MenuItem value="pass_type_name">Typ karnetu</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Kolejność</InputLabel>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    label="Kolejność"
                  >
                    <MenuItem value="asc">Rosnąco</MenuItem>
                    <MenuItem value="desc">Malejąco</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFilterActive(null);
                    setFilterPayed(null);
                    setFilterCreatedByAdmin(null);
                    setFilterPassType(null);
                    setFilterDateFrom("");
                    setFilterDateTo("");
                    setSortBy("created_at");
                    setSortOrder("desc");
                  }}
                >
                  Wyczyść filtry
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    if (selectedUser) {
                      invalidateUserCache(selectedUser.id);
                      fetchUserPasses(selectedUser.id);
                    }
                  }}
                >
                  Zastosuj
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Passes DataGrid */}
          {loadingPasses ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography ml={2}>Ładowanie karnetów...</Typography>
            </Box>
          ) : userPassesCache[selectedUser?.id] && userPassesCache[selectedUser.id].length > 0 ? (
            <Paper style={{ height: isMobile ? 400 : 500, width: "100%" }}>
              <DataGrid
                rows={userPassesCache[selectedUser.id]}
                columns={passColumns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: isMobile ? 5 : 10 },
                  },
                }}
                pageSizeOptions={isMobile ? [5] : [5, 10, 25]}
                getRowId={(row) => row.id}
                density={isMobile ? "compact" : "standard"}
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '6px 4px', sm: '8px' },
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 600,
                  }
                }}
              />
            </Paper>
          ) : (
            <Typography>Brak karnetów dla tego użytkownika.</Typography>
          )}
          {/* Add Pass Button */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              onClick={() => setAddPassDialogOpen(true)}
              color="primary"
              variant="contained"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Dodaj Karnet
            </Button>
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleCloseDialog} 
            color="error" 
            variant={isMobile ? "contained" : "text"}
            fullWidth={isMobile}
          >
            Zamknij
          </Button>
        </Box>
      </Dialog>

      {/* Dialog for adding pass */}
      <Dialog
        open={addPassDialogOpen}
        onClose={handleCloseAddPassDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Dodaj nowy karnet
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, overflowY: 'auto', maxHeight: '70vh' }}>
          {/* User Selection or Display */}
          {!selectedUser ? (
            <Autocomplete
              options={allUsers}
              getOptionLabel={(option) =>
                `${option.full_name} (${option.email})`
              }
              onChange={(event, newValue) => {
                setSelectedUserForPass(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Wybierz Użytkownika"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              )}
            />
          ) : (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                Użytkownik:
              </Typography>
              <Typography variant="body1">
                {selectedUser.full_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser.email}
              </Typography>
            </Box>
          )}

          {/* Pass Type Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="pass-select-label">Wybierz karnet</InputLabel>
            <Select
              labelId="pass-select-label"
              value={selectedPassType}
              onChange={handlePassTypeChange}
              label="Wybierz karnet"
            >
              {/* Event passes group - available first */}
              {passTypes.filter(p => p.is_event_pass && p.available_for_purchase !== false).length > 0 && (
                <MenuItem disabled sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  <EventIcon sx={{ mr: 1 }} /> Karnety Eventowe
                </MenuItem>
              )}
              {passTypes.filter(p => p.is_event_pass && p.available_for_purchase !== false).map((pass) => (
                <MenuItem key={pass.id} value={pass} sx={{ pl: 4 }}>
                  <EventIcon sx={{ mr: 1, fontSize: 18, color: 'secondary.main' }} />
                  {pass.name}
                </MenuItem>
              ))}
              
              {/* Separator if both types exist */}
              {passTypes.filter(p => p.is_event_pass && p.available_for_purchase !== false).length > 0 && 
               passTypes.filter(p => !p.is_event_pass && p.available_for_purchase !== false).length > 0 && (
                <MenuItem disabled sx={{ borderTop: '1px solid #ccc', mt: 1, pt: 1, fontWeight: 'bold' }}>
                  Standardowe Karnety
                </MenuItem>
              )}
              
              {/* Standard passes - available */}
              {passTypes.filter(p => !p.is_event_pass && p.available_for_purchase !== false).map((pass) => (
                <MenuItem key={pass.id} value={pass}>
                  {pass.name}
                </MenuItem>
              ))}

              {/* Unavailable passes section - at the bottom, visually dimmed */}
              {passTypes.filter(p => p.available_for_purchase === false).length > 0 && (
                <>
                  <MenuItem disabled sx={{ borderTop: '2px dashed #ccc', mt: 2, pt: 2, fontWeight: 'bold', color: 'text.disabled' }}>
                    <VisibilityOffIcon sx={{ mr: 1, fontSize: 18 }} /> Niedostępne w sklepie
                  </MenuItem>
                  
                  {/* Unavailable event passes */}
                  {passTypes.filter(p => p.is_event_pass && p.available_for_purchase === false).map((pass) => (
                    <MenuItem 
                      key={pass.id} 
                      value={pass} 
                      sx={{ 
                        pl: 4, 
                        opacity: 0.6,
                        bgcolor: '#f5f5f5',
                        '&:hover': { bgcolor: '#eeeeee' }
                      }}
                    >
                      <EventIcon sx={{ mr: 1, fontSize: 18, color: 'text.disabled' }} />
                      <span style={{ textDecoration: 'line-through', marginRight: 8 }}>{pass.name}</span>
                      <span style={{ fontSize: '0.75em', color: '#999' }}>(ukryty)</span>
                    </MenuItem>
                  ))}
                  
                  {/* Unavailable standard passes */}
                  {passTypes.filter(p => !p.is_event_pass && p.available_for_purchase === false).map((pass) => (
                    <MenuItem 
                      key={pass.id} 
                      value={pass} 
                      sx={{ 
                        pl: 4, 
                        opacity: 0.6,
                        bgcolor: '#f5f5f5',
                        '&:hover': { bgcolor: '#eeeeee' }
                      }}
                    >
                      <span style={{ textDecoration: 'line-through', marginRight: 8 }}>{pass.name}</span>
                      <span style={{ fontSize: '0.75em', color: '#999' }}>(ukryty)</span>
                    </MenuItem>
                  ))}
                </>
              )}
            </Select>
          </FormControl>

          {/* Event Pass Info */}
          {isEventPassMode && selectedPassType && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'secondary.light', borderRadius: 1, border: '2px solid', borderColor: 'secondary.main' }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 1 }}>
                <EventIcon color="secondary" /> Karnet Eventowy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Ten karnet jest przypisany do określonych eventów. Czas ważności zostanie automatycznie ustawiony na podstawie dat eventów.
              </Typography>
              {selectedPassType.events_for_pass_type && selectedPassType.events_for_pass_type.length > 0 ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Obejmuje eventy:
                  </Typography>
                  {selectedPassType.events_for_pass_type.map((event, idx) => (
                    <Box key={idx} sx={{ ml: 1 }}>
                      <Typography variant="body2">
                        • {event.event_name || `Event #${event.event_id}`}
                        {event.start_date && event.end_date && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({new Date(event.start_date).toLocaleDateString('pl-PL')} - {new Date(event.end_date).toLocaleDateString('pl-PL')})
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="warning.main">
                  Brak przypisanych eventów do tego karnetu.
                </Typography>
              )}
            </Box>
          )}

          {/* Lesson Categories - only for non-event passes */}
          {selectedPassType && !isEventPassMode &&
            selectedPassType.lesson_categories_for_pass_type?.map((category) => (
              <FormControl
                fullWidth
                sx={{ mb: 2 }}
                key={category.lesson_category_id}
              >
                <TextField
                  label={`${category.is_unlimited ? 'Nieograniczone lekcje' : 'Liczba lekcji'} dla kategorii: ${getLessonCategoryName(
                    category.lesson_category_id
                  )}`}
                  type={category.is_unlimited ? "text" : "number"}
                  variant="outlined"
                  value={
                    category.is_unlimited ? "Unlimited" : (lessonCategoryCounts[category.lesson_category_id] || "")
                  }
                  onChange={(e) => {
                    if (!category.is_unlimited) {
                      handleLessonCountChange(
                        category.lesson_category_id,
                        e.target.value
                      );
                    }
                  }}
                  fullWidth
                  InputProps={{
                    readOnly: category.is_unlimited || false,
                  }}
                  helperText={category.is_unlimited ? "Ten karnet oferuje nieograniczone lekcje dla tej kategorii" : ""}
                />
              </FormControl>
            ))}

          {/* Duration Selection - conditionally hidden */}
          {!shouldHideDuration ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  label="Długość trwania"
                  type="number"
                  variant="outlined"
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="duration-unit-label">Jednostka</InputLabel>
                <Select
                  labelId="duration-unit-label"
                  value={durationUnit}
                  onChange={(e) => setDurationUnit(e.target.value)}
                  label="Jednostka"
                >
                  <MenuItem value="days">Dni</MenuItem>
                  <MenuItem value="weeks">Tygodnie</MenuItem>
                  <MenuItem value="months">Miesiące</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                ℹ️ Czas ważności zostanie automatycznie obliczony na podstawie ustawień typu karnetu
                {selectedPassType?.month_restricted && (
                  <span> (ograniczenie miesięczne)</span>
                )}
                {selectedPassType?.purchase_period_restricted && (
                  <span> (ograniczenie okresu zakupu)</span>
                )}
              </Typography>
            </Box>
          )}

          {/* Month Availability Section */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={monthRestricted}
                  onChange={(e) => setMonthRestricted(e.target.checked)}
                  color="primary"
                />
              }
              label="Ograniczenie dostępności do konkretnych miesięcy"
            />

            {monthRestricted && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Wybierz dostępne miesiące:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {monthNames.map((monthName, index) => (
                    <FormControlLabel
                      key={index + 1}
                      control={
                        <Checkbox
                          size="small"
                          checked={availableMonths.includes(index + 1)}
                          onChange={() => handleMonthToggle(index + 1)}
                          color="primary"
                        />
                      }
                      label={monthName}
                      sx={{ fontSize: '0.875rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Purchase Period Availability Section */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchasePeriodRestricted}
                  onChange={(e) => setPurchasePeriodRestricted(e.target.checked)}
                  color="primary"
                />
              }
              label="Ograniczenie okresu zakupu"
            />

            {purchasePeriodRestricted && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Dostępny od"
                  type="datetime-local"
                  variant="outlined"
                  value={purchaseAvailableFrom}
                  onChange={(e) => setPurchaseAvailableFrom(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Dostępny do"
                  type="datetime-local"
                  variant="outlined"
                  value={purchaseAvailableUntil}
                  onChange={(e) => setPurchaseAvailableUntil(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Payed checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={payedValue}
                onChange={(e) => setPayedValue(e.target.checked)}
                color="primary"
              />
            }
            label="Opłacony"
          />

          {addPassError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {addPassError}
            </Alert>
          )}
          {addPassSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {addPassSuccess}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, p: 2 }}>
          <Button 
            onClick={handleCloseAddPassDialog} 
            color="error"
            fullWidth={isMobile}
            variant={isMobile ? "outlined" : "text"}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleAddPass}
            color="primary"
            variant="contained"
            fullWidth={isMobile}
            disabled={
              isAddingPass ||
              !selectedPassType ||
              // Only require duration fields when they're visible (not auto-calculated)
              (!shouldHideDuration && (!durationValue || !durationUnit || parseInt(durationValue, 10) <= 0)) ||
              (!selectedUser && !selectedUserForPass)
            }
          >
            {isAddingPass ? <CircularProgress size={24} /> : "Dodaj Karnet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for editing pass */}
      <Dialog
        open={editPassDialogOpen}
        onClose={() => setEditPassDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Edytuj Karnet
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Active Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={editingPass?.active || false}
                onChange={(e) =>
                  setEditingPass((prev) => ({
                    ...prev,
                    active: e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label="Aktywny"
          />

          {/* Pass Type Name */}
          <TextField
            label="Typ Karnetu"
            variant="outlined"
            value={editingPass?.pass_type_name || ""}
            onChange={(e) =>
              setEditingPass((prev) => ({
                ...prev,
                pass_type_name: e.target.value,
              }))
            }
            fullWidth
            margin="normal"
          />

          {/* Valid Until */}
          <TextField
            label="Ważny do"
            variant="outlined"
            type="date"
            value={
              editingPass?.valid_until
                ? editingPass.valid_until.split("T")[0]
                : ""
            }
            onChange={(e) => {
              const dateValue = e.target.value;
              setEditingPass((prev) => ({
                ...prev,
                valid_until: dateValue,
              }));
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Editing lesson categories */}
          {editingPass?.lesson_categories_for_pass &&
            editingPass.lesson_categories_for_pass.length > 0 && (
              <Box mt={2}>
                <Typography variant="h6">Kategorie Lekcji</Typography>
                {editingPass.lesson_categories_for_pass.map(
                  (category, index) => (
                    <Box key={index} mt={1}>
                      <TextField
                        label="Nazwa Kategorii"
                        variant="outlined"
                        value={category.name || ""}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      <TextField
                        label="Pozostałe Lekcje"
                        variant="outlined"
                        type={category.is_unlimited ? "text" : "number"}
                        value={category.is_unlimited ? "Nieograniczone" : category.remaining_lessons}
                        onChange={(e) => {
                          if (!category.is_unlimited) {
                            const newRemaining = parseInt(e.target.value, 10);
                            if (!isNaN(newRemaining)) {
                              setEditingPass((prev) => {
                                const newCategories = [
                                  ...prev.lesson_categories_for_pass,
                                ];
                                newCategories[index].remaining_lessons =
                                  newRemaining;
                                return {
                                  ...prev,
                                  lesson_categories_for_pass: newCategories,
                                };
                              });
                            }
                          }
                        }}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          readOnly: category.is_unlimited || false,
                        }}
                        helperText={category.is_unlimited ? "Ten karnet oferuje nieograniczone lekcje" : ""}
                      />
                      <TextField
                        label="Zużyte Lekcje"
                        variant="outlined"
                        type="number"
                        value={category.consumed_lessons}
                        onChange={(e) => {
                          const newConsumed = parseInt(e.target.value, 10);
                          if (!isNaN(newConsumed)) {
                            setEditingPass((prev) => {
                              const newCategories = [
                                ...prev.lesson_categories_for_pass,
                              ];
                              newCategories[index].consumed_lessons =
                                newConsumed;
                              return {
                                ...prev,
                                lesson_categories_for_pass: newCategories,
                              };
                            });
                          }
                        }}
                        fullWidth
                        margin="normal"
                      />
                    </Box>
                  )
                )}
              </Box>
            )}
        </DialogContent>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="flex-end" 
          alignItems="stretch" 
          gap={{ xs: 1, sm: 0 }}
          p={2}
        >
          <Button
            onClick={() => setEditPassDialogOpen(false)}
            color="error"
            variant="outlined"
            disabled={isUpdating}
            fullWidth={isMobile}
            sx={{ mr: { sm: 1 } }}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSaveEditPass}
            color="primary"
            variant="contained"
            disabled={isUpdating}
            fullWidth={isMobile}
          >
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Zapisz"
            )}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default UserPassesComponent;
